import type { SuperHubRouteParams } from '../types';
import type { ProjectContextValue, UserContextValue } from '@core/context';
import type { ReadCustomBlockGitCollectionType } from '@readme/api-contracts/schemas/customblock';
import type { GitSidebarCategory } from '@readme/api-contracts/schemas/sidebar';
import type { ReadGuideType } from '@readme/api/mappings/page/guide/types';
import type { HubResponseProps } from '@readme/backend/iso';

import { Suspense, type ReactNode, useContext, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ProjectContext, UserContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useRdmdOpts from '@core/hooks/useRdmdOpts';
import {
  ConnectSuperHubDocumentToApi,
  ConnectSuperHubSidebarToApi,
  InitializeSuperHubDocument,
  useProjectStore,
  useSuperHubStore,
} from '@core/store';
import getCurrentPageBreadcrumb from '@core/utils/Breadcrumb';
import { useScrollTarget } from '@core/utils/ScrollTarget';
import ScrollTop from '@core/utils/ScrollTop';
import DocBody from '@routes/Doc/Body';
import Header from '@routes/Doc/Header';
import '@routes/Doc/style.scss';
import APIErrorBoundary from '@ui/API/ErrorBoundary';
import Breadcrumb from '@ui/Breadcrumb';
import Footer from '@ui/DocFooter/SuperhubDocFooter';
import ErrorBoundary from '@ui/ErrorBoundary';
import Sidebar from '@ui/Sidebar';
import Spinner from '@ui/Spinner';
import XImportBlock from '@ui/XImportBlock/Wrap';

import SectionGate from '../components/PlanAccess/SectionGate';
import ConnectRoute from '../ConnectRoute';
import RedirectToEmptyParentChild from '../RedirectToEmptyParentChild';
import RedirectToSidebarFirstPage from '../RedirectToSidebarFirstPage';

import EmptyGuide from './Empty';
import styles from './index.module.scss';

/**
 * Shape of route specific data for guides pages.
 */
export interface DocRouteProps {
  apiDefinitions?: never;
  customBlocks: ReadCustomBlockGitCollectionType['data'];
  document: ReadGuideType['data'] | null;
  sidebar: GitSidebarCategory[];
}

function Content({ document: initialDocument, sidebar: initialSidebar, ...props }: HubResponseProps<DocRouteProps>) {
  const bem = useClassy(styles, 'SuperHubDoc');
  const { project } = useContext(ProjectContext) as ProjectContextValue;
  const { isGod } = useContext(UserContext) as unknown as UserContextValue;

  const [
    projectSubdomain,
    hideTOC,
    isFullWidth,
    enableSuggestedEdits,
    aiDropdownEnabled,
    breadcrumbEnabled,
    privacyView,
  ] = useProjectStore(s => [
    s.data.subdomain,
    s.data.appearance.table_of_contents === 'disabled',
    s.data.appearance.layout.full_width === 'enabled',
    s.data.suggested_edits === 'enabled',
    s.data.appearance.ai?.dropdown === 'enabled',
    s.data.appearance.navigation.breadcrumbs === 'enabled',
    s.data.privacy?.view,
  ]);

  // AI dropdown is only shown for public projects
  const showAIDropdown = aiDropdownEnabled && privacyView === 'public' && !project.flags?.hideAiFeatures;

  const [isLoadingDocument, isLoadingSidebar, document, sidebar, routeSection, isAdmin] = useSuperHubStore(s => {
    return [
      // Only consider the document + sidebar to be "loading" if the initial SSR
      // data is different than what's currently in our store. This prevents an
      // unnecessary FOUC or unnecessary loading spinners on initial page load.
      s.document.isLoading && s.document.data !== initialDocument,
      s.sidebar.isLoading && (!initialSidebar?.length || s.sidebar.data !== initialSidebar),
      s.document.getGuidesPageData(),
      s.sidebar.data,
      s.routeSection,
      s.user.role === 'admin',
    ];
  });

  const rdmdOpts = useRdmdOpts(document ? { isMdx: document.renderable.status } : undefined);
  const isInitialDocument = !!initialDocument && initialDocument?.slug === document?.slug;
  // On the initial route we get the precompiled body from SSR props. On soft-nav
  // those no longer match, so fall back to a `dehydrated` body carried on the
  // fetched document if one is present (e.g. a static export embeds it) — this
  // lets RMDXISH render without recompiling MDX. Live API responses omit the
  // field, so behavior there is unchanged.
  const dehydrated =
    (isInitialDocument ? props.rdmd?.dehydrated : undefined) ??
    (document as { dehydrated?: { body: string; css?: string; toc?: string } } | null)?.dehydrated;

  const isLoading = isLoadingDocument || isLoadingSidebar;

  const breadcrumbTrail = useMemo(
    () => getCurrentPageBreadcrumb(sidebar, document?.slug || ''),
    [sidebar, document?.slug],
  );

  // If we are on a loaded docs route with no slug and no document, that means
  // the `RedirectToSidebarFirstPage` HOC found an empty sidebar with no slug to
  // redirect to. Display the empty state in that case.
  const { slug } = useParams<SuperHubRouteParams>();
  if (!isLoading && !document && !slug) {
    return <EmptyGuide />;
  }

  // If the document is marked as not renderable, we can assume there is an MDX syntax error.
  const hasMdxError = !document?.renderable?.status;

  return (
    <main className={bem('&', 'rm-Guides')} id="content">
      <div className={bem('-container', 'rm-Container', 'rm-Container_flex', isFullWidth && 'rm-ContainerFull')}>
        {!isLoadingSidebar && <Sidebar activeDoc={document?.slug} categories={sidebar} pathRoot="docs" />}
        <article
          className={bem('-article', 'rm-Article', 'rm-Guides-SuperHub', isLoadingDocument && 'rm-Guides_loading')}
        >
          <Header
            breadcrumb={
              !!breadcrumbEnabled && (
                <Breadcrumb className={bem('-breadcrumb')}>
                  {breadcrumbTrail?.map(item =>
                    item.slug ? (
                      <Link key={item.slug} to={`${item.slug}`}>
                        {item.title}
                      </Link>
                    ) : (
                      <span key={item.title}>{item.title}</span>
                    ),
                  )}
                </Breadcrumb>
              )
            }
            excerpt={document?.content.excerpt || ''}
            githubLink={document?.href.github_url}
            lang={props.lang}
            slugUrl={document?.slug}
            suggestedEdits={!!enableSuggestedEdits && !!document?.href.github_url}
            superHub={true}
            title={document?.title}
          />

          {document && (
            <XImportBlock style={{ margin: '24px 0' }}>
              <DocBody
                dehydrated={dehydrated}
                docBody={document?.content?.body || ''}
                editUrl={`/update/${routeSection}/${document.slug}`}
                footer={
                  <Footer
                    document={document ?? undefined}
                    mdxishEnabled={project?.flags?.mdxish}
                    metricsThumbsEnabled={
                      // TODO: Add this to APIv2 project mapper so we can read
                      // this from ProjectStore instead of ProjectContext.
                      project.metrics.thumbsEnabled
                    }
                    projectSubdomain={projectSubdomain}
                    showRenderEngineBanner={isGod}
                    sidebar={sidebar}
                  />
                }
                hasMdxError={hasMdxError}
                hideTOC={hideTOC}
                isAdmin={isAdmin}
                mdx
                opts={rdmdOpts}
                showAIDropdown={showAIDropdown}
              />
            </XImportBlock>
          )}
        </article>
      </div>
    </main>
  );
}

function StreamingDocFallback({ sidebar }: { sidebar: GitSidebarCategory[] }) {
  const bem = useClassy(styles, 'SuperHubDoc');
  const isFullWidth = useProjectStore(s => s.data.appearance.layout.full_width === 'enabled');

  return (
    <main className={bem('&', 'rm-Guides')} id="content">
      <div className={bem('-container', 'rm-Container', 'rm-Container_flex', isFullWidth && 'rm-ContainerFull')}>
        {!!sidebar.length && <Sidebar categories={sidebar} pathRoot="docs" />}
        <article className={bem('-article', '-article_loading', 'rm-Article', 'rm-Guides-SuperHub')}>
          <Spinner size="xxl" />
        </article>
      </div>
    </main>
  );
}

function StreamingDocErrorFallback({
  errorCode,
  errorMessage,
  sidebar,
}: {
  errorCode?: number;
  errorMessage: ReactNode;
  sidebar: GitSidebarCategory[];
}) {
  const bem = useClassy(styles, 'SuperHubDoc');
  const isFullWidth = useProjectStore(s => s.data.appearance.layout.full_width === 'enabled');

  return (
    <main className={bem('&', 'rm-Guides')} id="content">
      <div className={bem('-container', 'rm-Container', 'rm-Container_flex', isFullWidth && 'rm-ContainerFull')}>
        {!!sidebar.length && <Sidebar categories={sidebar} pathRoot="docs" />}
        <article className={bem('-article', 'rm-Article', 'rm-Guides-SuperHub')}>
          <APIErrorBoundary errorCode={errorCode} errorMessage={errorMessage} padding="sm" />
        </article>
      </div>
    </main>
  );
}

/**
 * This is a fork of the @routes/Doc/index.jsx component that is used to render the a `/docs/:slug` page
 * when the `project.flags.superHub` is enabled.
 *
 * Because SuperHub uses APIv2 data, and the shape of that data is different from internal
 * Hub api data, we need to fork this component to handle the differences.
 */
export default function SuperHubDoc(props: HubResponseProps<DocRouteProps>) {
  const { document: initialDocument, customBlocks: initialCustomBlocks, sidebar: initialSidebar } = props;
  const shouldUseStreamingSuspense = !!props.isStreamingSSR && !initialDocument;
  const { ref } = useScrollTarget();

  const content = (
    <RedirectToSidebarFirstPage>
      <RedirectToEmptyParentChild>
        <InitializeSuperHubDocument customBlocks={initialCustomBlocks} document={initialDocument}>
          <ConnectSuperHubDocumentToApi suspense={shouldUseStreamingSuspense}>
            <SectionGate section="docs">
              <ScrollTop smooth target={ref} />
              <ConnectRoute next={Content} props={props} />
            </SectionGate>
          </ConnectSuperHubDocumentToApi>
        </InitializeSuperHubDocument>
      </RedirectToEmptyParentChild>
    </RedirectToSidebarFirstPage>
  );

  return (
    <ConnectSuperHubSidebarToApi initialSidebar={initialSidebar}>
      {shouldUseStreamingSuspense ? (
        <ErrorBoundary
          key={`superhub-doc-streaming-error-boundary-${props.reqUrl}`}
          fallbackRender={({ errorDetail }) => {
            const subject = `ReadMe Refactored Docs Error ${errorDetail.statusCode ? `(${errorDetail.statusCode})` : ''}`;
            const body = errorDetail.toString();

            return (
              <StreamingDocErrorFallback
                errorCode={errorDetail.statusCode}
                errorMessage={
                  <span>
                    Unable to load content.
                    <br />
                    <a
                      href={`mailto:support@readme.io?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      Please contact support@readme.io
                    </a>
                    <br />
                    with your error.
                  </span>
                }
                sidebar={initialSidebar}
              />
            );
          }}
        >
          <Suspense fallback={<StreamingDocFallback sidebar={initialSidebar} />}>{content}</Suspense>
        </ErrorBoundary>
      ) : (
        content
      )}
    </ConnectSuperHubSidebarToApi>
  );
}
